import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, List, ListItem, ListItemButton, ListItemText,
  CircularProgress, Button, Divider, IconButton, TextField, Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  EditOutlined as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { AgentChatClient } from '../../services/AgentChatClient';

interface SessionSummary {
  sessionId: string;
  updatedAt: string;
  title: string | null;
  preview: string;
  messageCount: number;
}

interface SessionsPanelProps {
  agentClient: AgentChatClient;
  onSelectSession: (sessionId: string) => void;
  onBack: () => void;
}

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

export const SessionsPanel: React.FC<SessionsPanelProps> = ({
  agentClient,
  onSelectSession,
  onBack,
}) => {
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);

  const fetchSessions = useCallback(async (cursor?: string) => {
    try {
      const result = await agentClient.getSessions(cursor);
      setSessions(prev => cursor ? [...prev, ...result.sessions] : result.sessions);
      setNextCursor(result.nextCursor);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [agentClient]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const handleLoadMore = () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    fetchSessions(nextCursor);
  };

  const startEditing = (s: SessionSummary) => {
    setEditingSessionId(s.sessionId);
    setEditValue(s.title || s.preview || '');
  };

  const cancelEditing = () => {
    setEditingSessionId(null);
    setEditValue('');
  };

  const handleSaveRename = async (sessionId: string) => {
    const trimmed = editValue.trim();
    if (!trimmed) return;
    setIsSavingTitle(true);
    try {
      const savedTitle = await agentClient.renameSession(sessionId, trimmed);
      setSessions(prev => prev.map(s => s.sessionId === sessionId ? { ...s, title: savedTitle } : s));
      setEditingSessionId(null);
      setEditValue('');
    } catch (error) {
      console.error('Failed to rename conversation:', error);
    } finally {
      setIsSavingTitle(false);
    }
  };

  const header = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
      }}
    >
      <IconButton size="small" onClick={onBack} aria-label="Back">
        <ArrowBackIcon fontSize="small" />
      </IconButton>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Past conversations
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {header}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4, flex: 1 }}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  if (sessions.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {header}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            No past conversations yet
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {header}
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        <List disablePadding>
          {sessions.map((s, i) => {
            const isEditing = editingSessionId === s.sessionId;
            const displayTitle = s.title || s.preview || 'New conversation';

            if (isEditing) {
              return (
                <React.Fragment key={s.sessionId}>
                  {i > 0 && <Divider />}
                  <Box sx={{ py: 1.5, px: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TextField
                        size="small"
                        autoFocus
                        fullWidth
                        value={editValue}
                        disabled={isSavingTitle}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); handleSaveRename(s.sessionId); }
                          if (e.key === 'Escape') { e.preventDefault(); cancelEditing(); }
                        }}
                        sx={{ flex: 1, '& .MuiInputBase-root': { height: 30, fontSize: '0.8rem' } }}
                      />
                      <Tooltip title="Save">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={isSavingTitle || !editValue.trim()}
                            onClick={() => handleSaveRename(s.sessionId)}
                          >
                            {isSavingTitle ? <CircularProgress size={14} /> : <CheckIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <span>
                          <IconButton size="small" disabled={isSavingTitle} onClick={cancelEditing}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.5 }}>
                      {formatDate(s.updatedAt)}
                    </Typography>
                  </Box>
                </React.Fragment>
              );
            }

            return (
              <React.Fragment key={s.sessionId}>
                {i > 0 && <Divider />}
                <ListItem disablePadding>
                  <ListItemButton onClick={() => onSelectSession(s.sessionId)} sx={{ py: 1.5, px: 2 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>
                            {displayTitle}
                          </Typography>
                          <Tooltip title="Rename conversation">
                            <IconButton
                              size="small"
                              aria-label="Rename conversation"
                              onClick={(e) => { e.stopPropagation(); startEditing(s); }}
                              sx={{ p: 0.25 }}
                            >
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', ml: 0.5 }}>
                            {s.messageCount} msg{s.messageCount !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {formatDate(s.updatedAt)}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>

        {nextCursor && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Button
              size="small"
              onClick={handleLoadMore}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={14} /> : undefined}
            >
              Load more conversations
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};
